import React from 'react';
import mova from 'mova';
import QuantityCounter from 'view/components/display/QuantityCounter/QuantityCounter';
import RadioGroup from 'view/components/inputs/RadioButton/RadioGroup';
import RadioButton from 'view/components/inputs/RadioButton/RadioButton';
import Checkbox from 'view/components/inputs/Checkbox/Checkbox';
import { upperFirst } from 'utils/utils';
import currency from '@filters/currency';

const t = mova.ns('containers.DishDetailsModal');

const DishOptions = ({ dishOptionCategories, formData, setFormData }) => {
  return dishOptionCategories.map((opCat) => {
    const catKey = `optionCatId-${opCat.id}`;
    const currentValues = formData[catKey] || {};

    const renderRadioOptions = () => (
      <>
        <p className='dish-details-modal__option-category-name'>{upperFirst(opCat.name)}</p>
        <p className='dish-details-modal__option-category-description'>{opCat.description}</p>
        <RadioGroup name={catKey} required={opCat.required} value={String(opCat.dishOptions[0]?.id)}>
          {(setValue, selected) =>
            opCat.dishOptions.map((op) => (
              <div
                key={op.id}
                className='dish-details-modal__option-wrapper'
                style={{ cursor: 'pointer' }}
                onClick={() => setValue(String(op.id))}
              >
                <div className='dish-details-modal__option-inf-wrapper'>
                  <p className='dish-details-modal__option-name'>{upperFirst(op.name)}</p>
                  <p className='dish-details-modal__option-price'>{currency(op.price)}</p>
                </div>
                <RadioButton
                  name={opCat.id}
                  value={op.id}
                  checked={selected === String(op.id)}
                  selected={selected}
                  setValue={setValue}
                  required={opCat.required}
                />
              </div>
            ))
          }
        </RadioGroup>
      </>
    );

    const renderCheckboxOptions = () => {
      const selectedCount = Object.values(currentValues).filter(Boolean).length;
      const maxSelection = opCat?.variables?.maxSelection;

      const limitReached = selectedCount >= maxSelection;

      return (
        <>
          <p className='dish-details-modal__option-category-name'>{upperFirst(opCat.name)}</p>
          <p className='dish-details-modal__option-category-description'>{opCat.description}</p>

          <p
            className='dish-details-modal__selection-info'
            style={{
              color: limitReached ? '#d9534f' : '#555D59',
              fontSize: '0.85rem',
              marginBottom: '0.5rem',
            }}>
            {limitReached && `${t('selectedOpt')}: ${maxSelection}`}
          </p>

          {opCat.dishOptions.map((op) => {
            const isChecked = currentValues[op.id] || false;
            const isDisabled = !isChecked && limitReached;

            return (
              <div
                key={op.id}
                className='dish-details-modal__option-wrapper'
                style={{
                  cursor: isDisabled ? 'not-allowed' : 'pointer',
                  opacity: isDisabled ? 0.6 : 1
                }}
                onClick={() => {
                  if (!isDisabled) {
                    setFormData((prev) => {
                      const updated = { ...prev };
                      if (!updated[catKey]) updated[catKey] = {};
                      updated[catKey][op.id] = !isChecked;
                      return updated;
                    });
                  }
                }}
              >
                <div className='dish-details-modal__option-inf-wrapper'>
                  <p className='dish-details-modal__option-name'>{upperFirst(op.name)}</p>
                  <p className='dish-details-modal__option-price'>{currency(op.price)}</p>
                </div>
                <div
                  className='dish-details-modal__checkbox'
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                >
                  <Checkbox
                    name={`optionCatId-${opCat.id}[${op.id}]`}
                    value={isChecked}
                    disabled={isDisabled}
                    setValue={(checked) => {
                      setFormData((prev) => {
                        const updated = { ...prev };
                        if (!updated[catKey]) updated[catKey] = {};
                        updated[catKey][op.id] = checked;
                        return updated;
                      });
                    }}
                  />
                </div>
              </div>
            );
          })}
        </>
      );
    };

    const renderQuantityOptions = () => {
      const maxSameOptionSelection = opCat?.variables?.maxSameOptionSelection;
      const maxSelection = opCat?.variables?.maxSelection || 1;

      // Calculate how many different options are selected (have at least 1 item)
      const selectedOptionsCount = Object.values(currentValues).filter(count => (count || 0) > 0).length;
      const limitReached = selectedOptionsCount >= maxSelection;

      return (
        <>
          <p className='dish-details-modal__option-category-name'>{upperFirst(opCat.name)}</p>
          <p className='dish-details-modal__option-category-description'>{opCat.description}</p>

          {limitReached && (
            <p
              className='dish-details-modal__selection-info'
              style={{
                color: '#d9534f',
                fontSize: '0.85rem',
                marginBottom: '0.5rem',
              }}>
              {t('selectedOpt')}: {maxSelection}
            </p>
          )}

          {opCat.dishOptions.map((op) => {
            const currentValue = currentValues[op.id] || 0;
            const isDisabled = limitReached && currentValue === 0;
            const hasItems = currentValue > 0;
            const individualLimitReached = currentValue >= maxSameOptionSelection;

            return (
              <div
                key={op.id}
                className='dish-details-modal__option-wrapper'
                style={{
                  cursor: hasItems ? 'default' : (isDisabled ? 'not-allowed' : 'pointer'),
                  opacity: isDisabled ? 0.6 : 1
                }}
                onClick={() => {
                  if (!isDisabled && !hasItems) {
                    // Add one item when clicking on card with no items
                    setFormData((prev) => {
                      const updated = { ...prev };
                      if (!updated[catKey]) updated[catKey] = {};
                      updated[catKey][op.id] = 1;
                      return updated;
                    });
                  }
                }}
              >
                <div className='dish-details-modal__option-inf-wrapper'>
                  <p className='dish-details-modal__option-name'>{upperFirst(op.name)}</p>
                  <p className='dish-details-modal__option-price'>{currency(op.price)}</p>
                </div>
                <div
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                >
                  <QuantityCounter
                    disable={isDisabled || individualLimitReached}
                    value={currentValue}
                    onChange={(delta) => {
                      setFormData((prev) => {
                        const updated = { ...prev };
                        if (!updated[catKey]) updated[catKey] = {};

                        const newCount = Math.max(0, (updated[catKey][op.id] || 0) + delta);

                        if (newCount === 0) {
                          delete updated[catKey][op.id];
                        } else {
                          updated[catKey][op.id] = newCount;
                        }

                        return { ...updated };
                      });
                    }}
                  />
                </div>
              </div>
            );
          })}
        </>
      );
    };

    if (opCat?.variables?.maxSameOptionSelection > 1) {
      return <div key={opCat.id}>{renderQuantityOptions()}</div>;
    }

    if (opCat?.variables?.maxSelection > 1) {
      return <div key={opCat.id}>{renderCheckboxOptions()}</div>;
    }

    if (opCat?.variables?.minSelection === 1) {
      return <div key={opCat.id}>{renderRadioOptions()}</div>;
    }

    return null;
  });
};

export default DishOptions;
